import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, updateCarDrawer, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { initiateAddlFGPromotion, filterOrphanedFreeGifts } from '@theme/mb-free-gift-promotions';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLineKey: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    // const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    // if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.key === line),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const cartItemRow = this.refs.cartItemRows.find((row) => row.dataset.key === line.toString());
    const itemType = cartItemRow?.dataset.itemType;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        // Handle removal of associated gift options
        if (quantity === 0) {
          const isCartUpdated = await this.removeGiftOptions(parsedResponseText, sectionsToUpdate, this.sectionId);
          if(isCartUpdated) return;
        }

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        if (parsedResponseText.items.length == 0) {
          this.handleUpdateCartShopLink()
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#resetAddToCartButtons(itemType);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the removal of associated gift options.
   * @param {Object} data - The cart response data.
   * @param {Set} sectionsToUpdate - The sections to update.
   * @param {string} sectionId - The section ID.
   * @returns {void}
   */
  async removeGiftOptions (data, sectionsToUpdate, sectionId) {
    if (data.items && data.items.length > 0) {
      let updates = {}
      const orphanedFGs = filterOrphanedFreeGifts(data.items);
      if(orphanedFGs.length > 0) { 
        orphanedFGs.forEach(freeGift => {
          updates[freeGift.key] = 0;
        })
      }

      // check addon gift validity
      const checkforInvalidAddons = await initiateAddlFGPromotion();
      if(checkforInvalidAddons && checkforInvalidAddons.length > 0) {
        updates = {
          ...updates,
          ...Object.fromEntries(checkforInvalidAddons.map((i) => [i.key, 0]))
        }
      };

      if (Object.keys(updates).length === 0) {
        return false;
      }

      try {
        const response = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Operation-Type": "Custom/CartUpdate",
          },
          body: JSON.stringify({
            updates,
            sections: Array.from(sectionsToUpdate).join(","),
            sectionId,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const updatedCart = await response.json();
        await morphSection(sectionId, updatedCart.sections[sectionId]);
        if(updatedCart.items.length == 0) {
          this.handleUpdateCartShopLink();
        }

        console.log("Cart Updated for free gifts");

        return true;
      } catch (error) {
        console.error("Error updating cart:", error);
        return false;
      }
    }
  }

  /**
   * Handles the update of cart drawer empty shop now button for active member
   */
  handleUpdateCartShopLink() {
    let isActiveMembership = JSON.parse(localStorage.getItem('isActiveMembership')) || false;
    const cartShopNowBtn = document.querySelector('.cart-items__empty-button');
    
    if (cartShopNowBtn && isActiveMembership) {
      cartShopNowBtn.setAttribute('href',"/collections/member-shop");
    }
}

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart subscription toggle error.
   * @param {string} key - The cart line key.
   * @param {Object} errorMessage - The error message.
   */
  handleSubscriptionToggleError = (key, errorMessage) => {
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${key}`];
    const cartItemError = this.refs[`cartItemError-${key}`];

    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');
    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');

    if (errorMessage.toLowerCase().includes('maximum limit')) {
      cartItemError.textContent = errorMessage
      cartItemErrorContainer.classList.remove('hidden');
    }
  }

  /**
   * Clears all cart item errors
   */
  clearCartErrors = () => {
    const cartItemErrorContainers = this.querySelectorAll(".cart-items__error");
    if(cartItemErrorContainers.length > 0) {
      cartItemErrorContainers.forEach(container => {
        if (container.classList.contains('hidden')) return;
        const cartItemError = container?.querySelector('.cart-item__error-text');
        cartItemError.textContent = '';
        container.classList.add('hidden');
      })
    }
  }

  /**
   * Resets the errors on all add to cart button for the checked purchase option
   */
  #resetAddToCartButtons(itemType) {
    const checkedInput = document.querySelector(
      `purchase-option[data-product-type="${itemType}"] .purchase-option-input:checked`
    );

    if (!checkedInput) return;

    const addToCartButtons = document.querySelectorAll('universal-add-to-cart');

    // Clear add to cart button errors
    if(addToCartButtons.length > 0) {
      addToCartButtons.forEach(button => {
        button.clearError?.(); 
      })
    }
  }

  /**
   * Handles the cart error.
   * @param {number} key - The cart line key
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (key, parsedResponseText) => {
    const quantitySelectors = this.refs.quantitySelectors;
    const quantitySelector = quantitySelectors.find(item => item.dataset.cartLineKey == key)
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${key}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${key}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
